/**
 * BentoTile — Statische Card mit Icon-Head, Content, Bottom-Action-Link (Sprint-16-Brand).
 *
 * Anatomie: Icon-Tile + Eyebrow-Label oben, Content dazwischen (flex-grow),
 * Action-Link mit „→" am unteren Rand (mt-auto — klebt am Boden auch wenn
 * Card in 2x2-Grid mehr Höhe bekommt).
 *
 * Icon-Tile-Farbcoding pro Card-Kategorie (Editorial-Diszplin auf Detail-Ebene):
 *   - navy    → Structure/Identity (Stammdaten)
 *   - sage    → Positive/Occupancy (Belegung)
 *   - sealwax → Network/Signal (Channels)
 *   - amber   → Money/Warmth (Preise, dark-variant)
 *
 * Varianten:
 *   - default: cream `bg-card` — Link in sealwax (Identity-Signature)
 *   - dark:    navy `bg-navy` mit paper-Text — Link hover-amber (Money-Signature)
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export type BentoIconTint = 'navy' | 'sage' | 'sealwax' | 'amber' | 'paper';

interface BentoTileProps {
  to: string;
  propertyId: string;
  icon: ReactNode;
  eyebrow: string;
  cta: string;
  variant?: 'default' | 'dark';
  /** Icon-Tile-Farbe. Default: sage (bleibt für Belegung). */
  iconTint?: BentoIconTint;
  children: ReactNode;
}

const iconTintClasses: Record<BentoIconTint, string> = {
  navy: 'bg-navy/12 text-navy',
  sage: 'bg-sage/15 text-sage',
  sealwax: 'bg-sealwax/15 text-sealwax',
  amber: 'bg-amber/20 text-amber',
  paper: 'bg-paper/10 text-paper/80',
};

export function BentoTile({
  to,
  propertyId,
  icon,
  eyebrow,
  cta,
  variant = 'default',
  iconTint = 'sage',
  children,
}: BentoTileProps) {
  const isDark = variant === 'dark';
  const effectiveTint: BentoIconTint = isDark ? 'paper' : iconTint;
  return (
    <div
      className={cn(
        'shadow-card flex flex-col rounded-lg p-6',
        isDark ? 'bg-navy text-paper' : 'bg-card border-line/50 text-ink border',
      )}
    >
      <div className="mb-4 flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            'grid size-6 shrink-0 place-items-center rounded-md',
            iconTintClasses[effectiveTint],
          )}
        >
          {icon}
        </span>
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-[0.12em]',
            isDark ? 'text-paper/50' : 'text-ink-soft',
          )}
        >
          {eyebrow}
        </span>
      </div>

      <div className="flex-1">{children}</div>

      <Link
        to={to}
        params={{ id: propertyId, propertyId }}
        className={cn(
          'group mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors',
          isDark ? 'text-paper hover:text-amber' : 'text-sealwax hover:text-sealwax/80',
        )}
      >
        {cta}
        <ArrowRight
          size={14}
          strokeWidth={2}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </Link>
    </div>
  );
}

export function BentoFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-faint text-[10px] font-semibold uppercase tracking-[0.12em]">
        {label}
      </dt>
      <dd className="text-ink mt-0.5 truncate text-sm font-medium">{value}</dd>
    </div>
  );
}
